import { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  CircleAlert,
  FileClock,
  Microchip,
  Navigation,
  Radio,
  Settings2,
  ShieldAlert,
} from 'lucide-react';
import Select from 'react-select';
import useMachinesOverview from '../../hooks/useMachinesOverview';
import MachineStatusOption, { getMachineStatus } from '../MachineStatusOption';
import CompanyScopePicker, { NO_COMPANY } from '../CompanyScopePicker';
import { getDirectionsUrl } from '../../utils/mapUtils';
import HubConfigSyncBar from './HubConfigSyncBar';
import styles from './MachineWorkspaceNav.module.scss';

const sections = [
  {
    label: 'Monitor',
    items: [
      { key: 'status', label: 'Status', icon: Activity, routeType: 'device' },
      { key: 'incidents', label: 'Incidents', icon: CircleAlert, routeType: 'machine' },
    ],
  },
  {
    label: 'Service records',
    items: [
      { key: 'notes', label: 'Notes', icon: FileClock, routeType: 'machine' },
      {
        key: 'imu-installation',
        label: 'IMU installation',
        icon: Microchip,
        routeType: 'machine',
      },
    ],
  },
  {
    label: 'Hub tools',
    items: [
      { key: 'config', label: 'Configuration', icon: Settings2, routeType: 'device' },
      { key: 'logs', label: 'Logs', icon: Radio, routeType: 'device' },
      {
        key: 'error-detect',
        label: 'Error detection',
        icon: ShieldAlert,
        routeType: 'device',
      },
    ],
  },
];

const getRouteContext = pathname => {
  const deviceMatch = pathname.match(/^\/device\/([^/]+)\/(status|config|logs|error-detect)/);
  if (deviceMatch) {
    return { serialNumber: deviceMatch[1], section: deviceMatch[2], routeType: 'device' };
  }

  const machineMatch = pathname.match(/^\/machine\/([^/]+)\/(incidents|notes|imu-installation)/);
  if (machineMatch) {
    return { machineId: machineMatch[1], section: machineMatch[2], routeType: 'machine' };
  }

  return null;
};

const getSectionPath = (item, machine) => {
  if (item.routeType === 'device' && machine?.serial_number) {
    return `/device/${machine.serial_number}/${item.key}`;
  }
  if (item.routeType === 'machine' && machine?.id !== undefined) {
    return `/machine/${machine.id}/${item.key}`;
  }
  return null;
};

function MachineWorkspaceNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const overview = useMachinesOverview();
  const context = useMemo(() => getRouteContext(location.pathname), [location.pathname]);
  const machines = useMemo(() => overview?.machines ?? [], [overview]);
  // Company scope filter for the "Switch machine" list. Empty = all companies.
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const machineOptions = useMemo(() => {
    const scoped =
      selectedCompanyIds.length === 0
        ? machines
        : machines.filter(machine => selectedCompanyIds.includes(machine.company_id || NO_COMPANY));
    return scoped.map(machine => {
      const normalizedMachine = {
        nickname: machine.nickname || machine.serial_number,
        serialNumber: machine.serial_number,
        companyName: machine.company_name,
        isOnline: Boolean(machine.is_online),
        inOperation: Boolean(machine.in_operation),
      };
      const status = getMachineStatus(normalizedMachine);
      return {
        value: String(machine.id),
        label: [
          normalizedMachine.nickname,
          normalizedMachine.serialNumber,
          normalizedMachine.companyName,
          status.label,
        ]
          .filter(Boolean)
          .join(' · '),
        machine: normalizedMachine,
      };
    });
  }, [machines, selectedCompanyIds]);

  const currentMachine = useMemo(() => {
    if (!context) {
      return null;
    }
    return machines.find(
      machine =>
        (context.serialNumber && machine.serial_number === context.serialNumber) ||
        (context.machineId && String(machine.id) === String(context.machineId))
    );
  }, [context, machines]);

  if (!context) {
    return null;
  }

  const machineName =
    currentMachine?.nickname ||
    currentMachine?.serial_number ||
    (context.machineId ? `Machine ${context.machineId}` : context.serialNumber);
  const directionsUrl = getDirectionsUrl(currentMachine?.location);

  const handleMachineChange = selectedOption => {
    const nextMachine = machines.find(machine => String(machine.id) === selectedOption?.value);
    if (!nextMachine) {
      return;
    }

    const currentItem = sections
      .flatMap(section => section.items)
      .find(item => item.key === context.section);
    const nextPath = currentItem ? getSectionPath(currentItem, nextMachine) : null;
    if (nextPath) {
      navigate(nextPath);
    }
  };

  return (
    <section className={styles.workspaceNav} aria-label="Machine workspace">
      <div className={styles.contextRow}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate('/')}
          aria-label="Back to fleet overview"
          title="Back to fleet overview"
        >
          <ArrowLeft size={19} />
        </button>

        <div className={styles.machineControls}>
          <div className={styles.companyScope}>
            <CompanyScopePicker
              selectedCompanyIds={selectedCompanyIds}
              setSelectedCompanyIds={setSelectedCompanyIds}
            />
          </div>

          <label className={styles.machineSwitcher}>
            <span>Switch machine</span>
            <Select
              inputId="workspace-machine-switcher"
              classNamePrefix="workspaceMachineSelect"
              options={machineOptions}
              value={
                machineOptions.find(option => option.value === String(currentMachine?.id)) || null
              }
              onChange={handleMachineChange}
              formatOptionLabel={(option, { context: optionContext }) => (
                <MachineStatusOption machine={option.machine} compact={optionContext === 'value'} />
              )}
              isSearchable
              isDisabled={machines.length === 0}
              placeholder={machineName || 'Loading machines…'}
              noOptionsMessage={() => 'No machines found'}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement="auto"
              maxMenuHeight={9999}
            />
          </label>

          {directionsUrl ? (
            <a
              className={styles.navigateButton}
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open last known position in Google Map"
            >
              <Navigation size={16} />
              <span>Navigate to this machine</span>
            </a>
          ) : (
            <span
              className={`${styles.navigateButton} ${styles.navigateDisabled}`}
              aria-disabled
              title="No known position"
            >
              <Navigation size={16} />
              <span>Navigate to this machine</span>
            </span>
          )}
        </div>
      </div>

      <nav className={styles.sectionNav} aria-label="Machine sections">
        {sections.map(section => (
          <div className={styles.sectionGroup} key={section.label}>
            <span className={styles.sectionLabel}>{section.label}</span>
            <div className={styles.sectionLinks}>
              {section.items.map(item => {
                const path = getSectionPath(item, currentMachine);
                if (!path) {
                  return null;
                }
                const Icon = item.icon;
                const incidentCount =
                  item.key === 'incidents' ? (currentMachine?.untaken_incident_count ?? 0) : 0;
                return (
                  <NavLink
                    key={item.key}
                    to={path}
                    className={({ isActive }) =>
                      `${styles.sectionLink} ${isActive ? styles.sectionLinkActive : ''}`
                    }
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                    {incidentCount > 0 && (
                      <span className={styles.incidentCount}>{incidentCount}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <HubConfigSyncBar serialNumber={context.serialNumber || currentMachine?.serial_number} />
    </section>
  );
}

export default MachineWorkspaceNav;
